import { z } from 'zod';
/**
 * The pages' read models. Each page reads one response, built from the computed sets the
 * collector writes (data-and-collection STO-8), so a page never calls GitHub or Teamwork.
 */
/** The shell around every page (SHL-001): who is signed in, and their inbox count. */
export const shellResponseSchema = z.object({
    /** The roster member the session belongs to; null when the signed-in person isn't on it. */
    member: z
        .object({
        userId: z.string(),
        displayName: z.string(),
        initials: z.string(),
        avatarColour: z.string(),
        roleLine: z.string(),
    })
        .nullable(),
    /** Admin or Member, from the roster entry's role (ACS-3). */
    roleLabel: z.enum(['Admin', 'Member']),
    /** Who the session says it is, for when there is no roster member. */
    signedInAs: z.string(),
    /** INB-7; null when there's no member, or nothing computed yet. */
    inboxCount: z.number().int().nonnegative().nullable(),
    freshness: z.lazy(() => freshnessSchema),
});
/** The data sources Pulse reads, as the collector names them. */
export const PULSE_SOURCES = ['github', 'teamwork', 'story-files', 'mini', 'google'];
/**
 * Which sources each figure is computed from (SHL-3): a figure is incomplete when any of them
 * failed in the last collection.
 */
export const FIGURE_SOURCES = {
    built: ['github', 'story-files', 'teamwork'],
    merged: ['github'],
    reviewed: ['github'],
    shipped: ['github'],
    openPullRequests: ['github'],
    waiting: ['github', 'teamwork'],
    stuck: ['github', 'teamwork'],
    workload: ['github', 'teamwork', 'story-files'],
    acceptance: ['story-files'],
    board: ['teamwork'],
    stories: ['story-files'],
    talos: ['mini'],
    meetings: ['google'],
};
/** When the data was last collected, and what failed (SHL-3). */
export const freshnessSchema = z.object({
    /** When the last collection finished; null before the first. */
    lastCollectedAt: z.string().nullable(),
    /** "4:15 PM today", or "Thu 8 Oct, 4:15 PM", in New York time; null before the first. */
    refreshedLabel: z.string().nullable(),
    /** SHL-4's as-of pages: "As of Sat 26 Sep, 12:11 PM", New York time; null before the first. */
    asOfLabel: z.string().nullable(),
    /** No collection, or the last one finished more than 90 minutes ago. */
    stale: z.boolean(),
    /** Sources that failed in the last collection, each with what may be missing. */
    failures: z.array(z.object({ source: z.enum(PULSE_SOURCES), message: z.string() })),
});
const linkRow = z.object({
    /** "shaw-platform #904 · RTE-107" */
    ref: z.string(),
    url: z.string(),
    title: z.string(),
    /** "Waiting on Sumit" or similar; null when the row doesn't need one. */
    sub: z.string().nullable(),
});
/** Today (TOD-1 to TOD-8): the team's day, for one business date. */
export const todayResponseSchema = z.object({
    date: z.string(),
    /** "Friday 25 September" */
    title: z.string(),
    tiles: z.object({
        merged: z.object({ count: z.number().int(), breakdown: z.string() }),
        shippedToProduction: z.object({ count: z.number().int(), sub: z.string() }),
        waitingOnYou: z.object({ count: z.number().int().nullable() }),
        talosWorkItems: z.object({ count: z.number().int().nullable(), sub: z.string(), unavailableReason: z.string().nullable() }),
    }),
    people: z.array(z.object({
        userId: z.string(),
        displayName: z.string(),
        initials: z.string(),
        avatarColour: z.string(),
        roleLine: z.string(),
        isAgent: z.boolean(),
        merged: z.number().int(),
        openPullRequests: z.number().int().nullable(),
        reviewed: z.number().int(),
        lines: z.array(z.string()),
        /** The day's blockers note (NOTE-1); null shows "Not added yet". */
        note: z.string().nullable(),
        href: z.string(),
    })),
    attention: z.object({
        /** False on a past day with no stored end-of-day state (SHL-5). */
        recorded: z.boolean(),
        groups: z.array(z.object({ group: z.string(), tone: z.enum(['c-warn', 'c-changes']), rows: z.array(linkRow) })),
        riskFlags: z.array(z.object({ statement: z.string(), detail: z.string(), link: z.string() })),
        stuck: z.array(linkRow),
        idleLine: z.string().nullable(),
    }),
    shipped: z.object({
        /** "Friday" */
        weekday: z.string(),
        /** PRs delivered to production that day (SHP-6), housekeeping included. */
        count: z.number().int(),
        live: z.array(linkRow),
        housekeepingCount: z.number().int(),
        laterDays: z.array(z.string()),
        toDev: z.array(z.object({ productName: z.string(), count: z.number().int(), production: z.string(), warn: z.boolean() })),
    }),
    projects: z.array(z.object({ productName: z.string(), line: z.string(), people: z.string(), href: z.string() })),
});
/** WL-2's states, in the order a workload bar stacks them (the prototype's WL_STATES). */
export const WORKLOAD_STATES = ['Overdue', 'In progress', 'Ready or due', 'In review', 'Blocked or waiting', 'Backlog, no date'];
const personCard = todayResponseSchema.shape.people.element;
const workloadSummary = z.object({
    counts: z.record(z.enum(WORKLOAD_STATES), z.number().int()),
    total: z.number().int(),
    /** "2 Teamwork tasks · 3 Shaw stories · 1 GitHub issue" */
    sources: z.string(),
});
/** People's Everyone tab (PPL-1 to PPL-3): workload on one scale, then a card per member. */
export const peopleResponseSchema = z.object({
    date: z.string(),
    /** "Friday 25 September" */
    dateLabel: z.string(),
    /** False on a past day with no stored end-of-day state (SHL-5). */
    recorded: z.boolean(),
    workload: z.array(z.object({
        userId: z.string(),
        displayName: z.string(),
        initials: z.string(),
        avatarColour: z.string(),
        href: z.string(),
        next: z.string().nullable(),
        summary: workloadSummary,
    })),
    cards: z.array(personCard),
});
const dayRow = z.object({
    ref: z.string(),
    url: z.string(),
    storyId: z.string().nullable(),
    title: z.string(),
    kind: z.string().nullable(),
    state: z.string().nullable(),
    note: z.string().nullable(),
});
/** One member's day (PER-1 to PER-5). */
export const personResponseSchema = z.object({
    date: z.string(),
    dateLabel: z.string(),
    member: z.object({ userId: z.string(), displayName: z.string(), roleLine: z.string(), isAgent: z.boolean() }),
    tiles: z.object({ builtToday: z.number().int(), merged: z.number().int(), reviewed: z.number().int(), openPullRequests: z.number().int().nullable() }),
    thisWeek: z.object({
        built: z.number().int(),
        lastWeek: z.number().int(),
        finished: z.number().int(),
        open: z.number().int(),
        unlinked: z.number().int(),
        unlinkedFlagged: z.boolean(),
        items: z.array(z.object({ ref: z.string(), title: z.string(), url: z.string(), repositoryName: z.string().nullable(), kind: z.string(), finished: z.boolean() })),
    }),
    sections: z.array(z.object({
        productName: z.string(),
        groups: z.array(z.object({ title: z.string(), rows: z.array(dayRow), collapsed: z.string().nullable(), note: z.string().nullable() })),
    })),
    /** False on a past day with no stored end-of-day state: the side panels show "Not recorded". */
    recorded: z.boolean(),
    nextUp: z.array(z.object({ title: z.string(), where: z.string(), url: z.string(), due: z.object({ text: z.string(), tone: z.enum(['late', 'soon', 'none']) }), tag: z.string().nullable() })),
    nextUpNote: z.string().nullable(),
    workload: workloadSummary,
    note: z.string().nullable(),
    /** True on the signed-in member's own page for today: the only note they can write (NOTE-1, NOTE-4). */
    noteEditable: z.boolean(),
    waitingOnOthers: z.array(z.object({ who: z.string(), items: z.array(linkRow) })),
    stuck: z.array(linkRow),
});
/** People's Planning gaps tab (PPL-4): as of now, where planning data is thin. */
export const planningResponseSchema = z.object({
    /** False until a collection run has computed the gaps. */
    recorded: z.boolean(),
    noDueDate: z.array(z.object({ userId: z.string(), displayName: z.string(), href: z.string(), withoutDueDate: z.number().int(), open: z.number().int() })),
    /** Each overdue task: ref "Satyam · due 20 May", the task's title and link. */
    overdue: z.array(linkRow),
    /** A line per member with tasks due today, linking to their person page. */
    dueToday: z.array(linkRow),
    /** "7 GitHub issues and 16 Teamwork tasks; 11 review requests older than 30 days" */
    oldAssignments: z.array(z.object({ userId: z.string(), displayName: z.string(), line: z.string() })),
    /** One per story repository with unassigned stories. */
    unassignedStories: z.array(z.object({ productName: z.string(), total: z.number().int(), line: z.string() })),
});
/** SHP-P6: a period's narrative: Talos's for a finished period, else Pulse's summary. */
const shippedNarrative = z.object({
    text: z.string(),
    by: z.enum(['talos', 'pulse']),
    /** "Written by Talos · Mon 21 Sep", or Pulse's summary saying when Talos writes. */
    caption: z.string(),
});
/** A story or task a project delivered, or has on dev (SHP-P3). */
const shippedItem = z.object({
    /** "PLF-212", "Task 45021" */
    ref: z.string(),
    title: z.string(),
    url: z.string(),
    prs: z.number().int(),
    /** When it was delivered: "Fri", "Tue–Thu", "Week of 21 Sep", "3 weeks"; null for work in dev. */
    when: z.string().nullable(),
});
/** Shipped (SHP-P1 to SHP-P6): what each project delivered in a week or a month, and what is on dev. */
export const shippedResponseSchema = z.object({
    view: z.enum(['week', 'month']),
    /** The week's Monday or the month's first day. */
    period: z.string(),
    /** "week of 21 September", "September" */
    label: z.string(),
    isCurrent: z.boolean(),
    /** The periods the stepper moves to; `next` is null on the current period. */
    previous: z.string(),
    next: z.string().nullable(),
    /** SHP-P2: four tiles. */
    tiles: z.array(z.object({ label: z.string(), value: z.number().int(), sub: z.string(), warn: z.boolean() })),
    team: shippedNarrative,
    /** SHP-P4: a month's deliveries week by week; null for a week. */
    weeks: z.array(z.object({ label: z.string(), count: z.number().int() })).nullable(),
    /** SHP-P3: the most delivered first. */
    projects: z.array(z.object({
        /** The project's slug, for its page. */
        key: z.string(),
        name: z.string(),
        toProduction: z.number().int(),
        toDev: z.number().int(),
        narrative: shippedNarrative,
        /** Its delivered work, grouped by story or task: what Talos writes the narrative from (SHP-006). */
        delivered: z.array(shippedItem),
        otherFixes: z.array(z.object({ text: z.string(), url: z.string() })),
        housekeeping: z.number().int(),
        /** SHP-P3, SHP-P8: its releases into production, and PRs merged straight into it, newest first. */
        releases: z.array(z.object({
            repositoryName: z.string(),
            number: z.number().int(),
            /** The business date it reached production, for its view's link. */
            date: z.string(),
            /** "Fri 25 Sep" */
            dateLabel: z.string(),
            /** Talos's line's first sentence, else its first stories and tasks. */
            line: z.string(),
            prs: z.number().int(),
            /** Merged straight into production, not through the dev branch. */
            direct: z.boolean(),
        })),
        inDev: z.array(shippedItem),
        /** SHP-P5: the plain text "Copy for a client update" copies; null when nothing was delivered. */
        copy: z.string().nullable(),
    })),
});
/** SHP-P8: one PR merged into production, and what it brought there, written for a client update. */
export const shippedReleaseResponseSchema = z.object({
    project: z.object({ slug: z.string(), name: z.string() }),
    repositoryName: z.string(),
    number: z.number().int(),
    title: z.string(),
    url: z.string(),
    direct: z.boolean(),
    /** "Release of Friday 25 September", or "Straight to main, Thursday 24 September". */
    heading: z.string(),
    /** "Fri 25 Sep, 4:12 PM" */
    whenLabel: z.string(),
    /** The update: Talos's line, or Pulse's summary. */
    update: shippedNarrative,
    /** What's new, in plain words: its stories and tasks, then its other fixes. */
    whatsNew: z.array(z.string()),
    /** See each task. */
    tasks: z.array(z.object({ ref: z.string(), title: z.string(), url: z.string(), prs: z.number().int() })),
    otherFixes: z.array(z.object({ text: z.string(), url: z.string() })),
    housekeeping: z.number().int(),
    prs: z.number().int(),
    /** What "Copy for a client update" copies. */
    copy: z.string(),
});
/** Inbox (INB-1 to INB-9): what waits on the signed-in member, as of now. */
export const inboxResponseSchema = z.object({
    /** "Saturday 26 September" */
    dateLabel: z.string(),
    /** The member's first name; null when the signed-in person isn't on the roster. */
    memberName: z.string().nullable(),
    /** False until a collection run has computed the inbox. */
    recorded: z.boolean(),
    /** INB-7: the badge's count. */
    count: z.number().int(),
    /** INB-8: in the spec's order; INB-9: groups with nothing in them are left out. */
    groups: z.array(z.object({
        group: z.string(),
        why: z.string(),
        tone: z.enum(['c-warn', 'c-changes', 'c-kind']),
        /** False for "Your PRs with no reviewer", which the badge leaves out. */
        counted: z.boolean(),
        rows: z.array(linkRow),
    })),
    /** INB-6: for members who accept stories; null for everyone else. */
    acceptance: z.object({ waiting: z.number().int(), line: z.string(), href: z.string() }).nullable(),
    stale: z.object({ count: z.number().int(), line: z.string(), url: z.string() }).nullable(),
    mentions: z.array(linkRow),
});
const boardCard = z.object({
    /** "RTE-110 +3"; null when the card names no story. */
    sid: z.string().nullable(),
    title: z.string(),
    source: z.enum(['GitHub', 'Teamwork']),
    /** "Issue #949", "PR #931" or "Task 44188597". */
    sourceLabel: z.string(),
    url: z.string(),
    /** The author, or the task's assignees, by first name. */
    who: z.string(),
    /** The PR beside the author, when the card opens something else. */
    pullRequest: z.object({ label: z.string(), url: z.string() }).nullable(),
    state: z.string(),
});
/** Projects' Board tab (PRJ-1 to PRJ-4) for a product and business date. */
export const projectBoardResponseSchema = z.object({
    date: z.string(),
    dateLabel: z.string(),
    /** False when that day's boards weren't computed. */
    recorded: z.boolean(),
    /** PRJ-1: one tab per product with a board; Domains and Acceptance need a story repository. */
    products: z.array(z.object({ slug: z.string(), name: z.string(), hasStories: z.boolean() })),
    product: z.object({ slug: z.string(), name: z.string(), sub: z.string(), hasStories: z.boolean() }).nullable(),
    columns: z.array(z.object({ title: z.string(), hint: z.string(), cards: z.array(boardCard) })),
    contributions: z.array(z.object({ userId: z.string(), name: z.string(), initials: z.string(), avatarColour: z.string(), line: z.string() })),
    /** PRJ-4: what the cards open. */
    linkNote: z.string(),
});
const projectTab = z.object({ slug: z.string(), name: z.string(), hasStories: z.boolean() });
const kpiTile = z.object({ label: z.string(), value: z.number().int(), sub: z.string(), warn: z.boolean() });
/** PRJ-5's statuses, in the order the bar stacks them. */
export const STORY_STATUSES = ['draft', 'ready-for-dev', 'in-progress', 'in-review', 'accepted', 'blocked'];
/** Projects' Domains tab (PRJ-5): a story repository's stories by domain, as of now. */
export const projectDomainsResponseSchema = z.object({
    products: z.array(projectTab),
    /** Null when the product has no story repository. */
    product: z.object({ slug: z.string(), name: z.string(), repositoryName: z.string() }).nullable(),
    recorded: z.boolean(),
    tiles: z.array(kpiTile),
    domains: z.array(z.object({
        domain: z.string(),
        code: z.string().nullable(),
        byStatus: z.record(z.string(), z.number().int()),
        /** Stories, archived left out. */
        total: z.number().int(),
        acPassing: z.number().int(),
        acTotal: z.number().int(),
        folderUrl: z.string(),
    })),
    blocked: z.array(linkRow),
});
/** Projects' Acceptance tab (PRJ-6): a story repository's acceptance queue, as of now. */
export const projectAcceptanceResponseSchema = z.object({
    products: z.array(projectTab),
    product: z.object({ slug: z.string(), name: z.string(), repositoryName: z.string() }).nullable(),
    recorded: z.boolean(),
    tiles: z.array(kpiTile),
    /** Every waiting story, longest waiting first; the page shows 25 at a time. */
    oldest: z.array(z.object({ id: z.string(), title: z.string(), domain: z.string(), assignee: z.string(), days: z.number().int().nullable(), url: z.string() })),
    buckets: z.array(z.object({ label: z.string(), count: z.number().int(), tone: z.string() })),
    /** The last 4 weeks, oldest first. */
    weeks: z.array(z.object({ label: z.string(), intoReview: z.number().int(), accepted: z.number().int() })),
    /** Who owns them: waiting stories per owner, the person who accepts them (PRJ-005 AC-5). */
    owners: z.array(z.object({ name: z.string(), count: z.number().int() })),
});
const trendSeries = z.object({ name: z.string(), colour: z.string(), values: z.array(z.number().int()) });
/** Trends (TRN-1 to TRN-5): how the last 4, 8 or 12 weeks compare. No commit or line counts. */
export const trendsResponseSchema = z.object({
    /** TRN-1: weeks (the default) or months. */
    view: z.enum(['week', 'month']),
    /** The last period shown: a week's Monday or a month's first day. */
    period: z.string(),
    /** The periods previous and next move to; `next` is null on the current period. */
    previous: z.string(),
    next: z.string().nullable(),
    isCurrent: z.boolean(),
    /** "last 4 weeks", or "Jul to Oct 2026". */
    rangeLabel: z.string(),
    /** The periods shown, oldest first. */
    periods: z.array(z.object({ key: z.string(), label: z.string() })),
    /** "Weeks run Monday to Sunday in New York time. The week of 5 Oct runs to Friday 9." */
    currentNote: z.string(),
    /** Periods with no stored figures, by label. */
    missing: z.array(z.string()),
    built: z.object({
        tiles: z.array(kpiTile.extend({ value: z.union([z.number().int(), z.string()]) })),
        perWeek: z.array(z.object({ finished: z.number().int(), open: z.number().int() })),
        members: z.array(z.object({
            userId: z.string(),
            name: z.string(),
            colour: z.string(),
            href: z.string(),
            perWeek: z.array(z.object({ items: z.number().int(), finished: z.number().int(), open: z.number().int() })),
            thisWeek: z.object({ items: z.number().int(), finished: z.number().int(), open: z.number().int(), stories: z.number().int(), tasks: z.number().int(), unlinked: z.number().int() }),
            unlinkedPercent: z.number().int(),
            unlinkedFlagged: z.boolean(),
        })),
    }),
    merges: z.object({
        tiles: z.array(kpiTile.extend({ value: z.union([z.number().int(), z.string()]) })),
        merged: z.array(trendSeries),
        reviews: z.array(trendSeries),
    }),
    side: z.object({
        toProduction: z.array(z.number().int()),
        /**
         * TRN-003 AC-2: Built per project, busiest first; an item counts once in its project per column,
         * however many people worked on it. A project with nothing built in the range is left out.
         */
        projects: z.array(z.object({
            productName: z.string(),
            href: z.string(),
            perWeek: z.array(z.object({ items: z.number().int(), finished: z.number().int(), open: z.number().int() })),
            thisWeek: z.object({ items: z.number().int(), finished: z.number().int(), open: z.number().int(), stories: z.number().int(), tasks: z.number().int(), unlinked: z.number().int() }),
            unlinkedPercent: z.number().int(),
            unlinkedFlagged: z.boolean(),
        })),
        cycle: z.array(z.object({ name: z.string(), medianHours: z.number(), p75Hours: z.number(), count: z.number().int() })),
    }),
});
/** The Talos page (TLS-1 to TLS-9): the agent's status, health and day, as of now. */
export const talosResponseSchema = z.object({
    /** False until the Mini has reported (COL-010). */
    reported: z.boolean(),
    /** TLS-1: from the stop switch; null before the first report. */
    status: z.enum(['Running', 'Stopped']).nullable(),
    /** "Checked Fri 9 Oct, 11:25 PM." */
    checkedLabel: z.string().nullable(),
    /** TLS-2: last run, stop switch, Docker, the Mini, schedule and locks. */
    health: z.array(z.object({ label: z.string(), value: z.string(), detail: z.string(), warn: z.boolean() })),
    tiles: z.array(kpiTile.extend({ value: z.union([z.number().int(), z.string()]) })),
    /** TLS-4 and TLS-5, as on a Person page; null when Talos isn't on the roster. */
    person: personResponseSchema.nullable(),
    /** TLS-5: the ready-for-dev stories and tasks assigned to Talos, in priority order. */
    queue: personResponseSchema.shape.nextUp,
    /** TLS-6: work items per hour, New York time, midnight first. */
    byHour: z.array(z.number().int()),
    /** "5 PM, 21 items"; null on a day with none. */
    busiestHour: z.string().nullable(),
    /** TLS-7 */
    byRole: z.array(z.object({ role: z.string(), count: z.number().int() })),
    /** TLS-8: failed run-log lines, then CI failures on Talos's PRs with what fixed them. */
    failures: z.array(linkRow),
    /** TLS-9: Talos's open PRs held or waiting on review. */
    waiting: z.array(linkRow),
});
/** TLS-10: the last 24 hours (the default) or the last 7 days. */
export const talosCapacityQuerySchema = z.object({ range: z.enum(['day', 'week']).default('day') });
/** TLS-10: whether the Mini can take more work, from its usage samples (TLS-D8). */
export const talosCapacityResponseSchema = z.object({
    /** False until the Mini has sent a sample. */
    reported: z.boolean(),
    range: talosCapacityQuerySchema.shape.range.removeDefault(),
    /** "Last sample Fri 9 Oct, 11:25 PM."; null before the first. */
    sampledLabel: z.string().nullable(),
    verdict: z.string(),
    /** CPU, memory, at capacity, most runs at once. */
    cells: z.array(z.object({ label: z.string(), value: z.string(), detail: z.string(), warn: z.boolean() })),
    /** A sample each over 24 hours, an hour's average each over 7 days; `position` is 0 to 1 across the range. */
    points: z.array(z.object({
        position: z.number().min(0).max(1),
        cpu: z.number(),
        memory: z.number(),
        full: z.boolean(),
        running: z.array(z.string()),
        /** "Fri 9 Oct, 11:25 PM · CPU 42% · memory 61%" */
        label: z.string(),
        /** True when samples are missing before it, so the line breaks. */
        gapBefore: z.boolean(),
    })),
    /** The time axis: every 3 hours over 24 hours, each midnight over 7 days, New York time. */
    ticks: z.array(z.object({ label: z.string(), position: z.number().min(0).max(1) })),
});
//# sourceMappingURL=pages.js.map